import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "@fortawesome/fontawesome-free/css/all.min.css";
import CourseService from "../../services/CourseService";
import { formatDate, parseDataBlocks } from "../../utils/helpers";
import "./AvailablesCourses.css";

const PAGE_SIZE = 9;
const SEARCH_DEBOUNCE_MS = 350;

const TYPE_OPTIONS = [
  { label: "All types", value: "" },
  { label: "Course", value: "SIMPLE" },
  { label: "Session", value: "SESSION" },
];

const STATUS_OPTIONS = [
  { label: "All statuses", value: "" },
  { label: "Opened", value: "OPENED" },
  { label: "Closed", value: "CLOSED" },
];

const getCourseIdPath = (item) => `${item?.slug || "course"}-${item?.id}`;

const getDetailsPath = (item) => {
  if (item?.type === "SESSION") {
    return `/sessions/next/${getCourseIdPath(item)}`;
  }

  return `/courses/next/${getCourseIdPath(item)}`;
};

const getTypeLabel = (type) => (type === "SESSION" ? "Live Session" : "Course");

const getStatusLabel = (status) => (status === "CLOSED" ? "Closed" : "Opened");

const getCourseLessonsLabel = (item) => {
  if (!item?.numberLessons) return "0 lessons";

  return `${item.numberLessons} lesson${Number(item.numberLessons) > 1 ? "s" : ""}`;
};

const getSessionStartDateLabel = (item) =>
  item?.startDate ? formatDate(item.startDate, 2) : "To be announced";

const getSessionDurationLabel = (item) => {
  if (!item?.startDate || !item?.endDate) return "Live cohort";

  const start = new Date(item.startDate);
  const end = new Date(item.endDate);
  const diffInMs = end.getTime() - start.getTime();

  if (!Number.isFinite(diffInMs) || diffInMs <= 0) return "Live cohort";

  const weeks = Math.max(1, Math.ceil(diffInMs / (1000 * 60 * 60 * 24 * 7)));

  return `${weeks} week${weeks > 1 ? "s" : ""}`;
};

const getRichTextOthersShortDescription = (richTextOthers) => {
  const blocks = parseDataBlocks(richTextOthers);
  const directShortDescription = blocks.find((block) =>
    Boolean(block?.shortDescription)
  )?.shortDescription;

  if (directShortDescription) return directShortDescription;

  const shortDescriptionBlock = blocks.find(
    (block) => block?.section?.toLowerCase() === "shortdescription"
  );

  return shortDescriptionBlock?.description || shortDescriptionBlock?.text || "";
};

const getDescriptionPreview = (item) => {
  const description =
    getRichTextOthersShortDescription(item?.richTextOthers) || item?.description;
  const plainText = String(description || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|&#160;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!plainText) {
    return "Explore this Utrains learning item and review the details before joining.";
  }

  return plainText.length > 128 ? `${plainText.slice(0, 128)}...` : plainText;
};

function AvailablesCourse() {
  const [items, setItems] = useState([]);
  const [metaData, setMetaData] = useState({
    page: 0,
    numberPerPage: PAGE_SIZE,
    totalNumberOfPage: 0,
    totalResult: 0,
  });
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(0);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const getCatalogItems = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await CourseService.getAllSimplesCourses({
          page,
          numberPerPage: PAGE_SIZE,
          type: selectedType,
          status: selectedStatus,
          search: debouncedSearch,
        });

        if (response.status === "success") {
          const data = response.data || {};
          setItems(Array.isArray(data.result) ? data.result : []);
          setMetaData({
            page: data.page || 0,
            numberPerPage: data.numberPerPage || PAGE_SIZE,
            totalNumberOfPage: data.totalNumberOfPage || 0,
            totalResult: data.totalResult || 0,
          });
        } else {
          setItems([]);
          setErrorMessage("Unable to load courses and sessions right now.");
        }
      } catch (error) {
        setItems([]);
        setErrorMessage("Unable to load courses and sessions right now.");
      } finally {
        setIsLoading(false);
      }
    };

    getCatalogItems();
  }, [page, selectedType, selectedStatus, debouncedSearch]);

  const totalPages = metaData.totalNumberOfPage || 0;
  const totalResults = metaData.totalResult || 0;
  const showingStart = totalResults === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingEnd = Math.min((page + 1) * PAGE_SIZE, totalResults);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 1) return [];

    return Array.from({ length: totalPages }, (_, index) => index).filter(
      (pageNumber) =>
        pageNumber === 0 ||
        pageNumber === totalPages - 1 ||
        Math.abs(pageNumber - page) <= 1
    );
  }, [page, totalPages]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setDebouncedSearch(searchInput.trim());
    setPage(0);
  };

  const selectType = (value) => {
    setSelectedType(value);
    setPage(0);
  };

  const selectStatus = (value) => {
    setSelectedStatus(value);
    setPage(0);
  };

  return (
    <main className="available-catalog-page">
      <div className="ac-breadcrumb-band">
        <div className="ac-container ac-breadcrumb">
          <Link to="/">Home</Link>
          <i className="fa-solid fa-chevron-right"></i>
          <strong>Courses & Sessions</strong>
        </div>
      </div>

      <section className="ac-hero">
        <div className="ac-container">
          <div
            className="ac-hero-shell"
            style={{
              "--ac-hero-image": `url(${process.env.PUBLIC_URL}/assets/images/banners/llmops-course-hero-1600x600.webp)`,
            }}
          >
            <div className="ac-hero-content">
              <span className="ac-eyebrow">
                <i className="fa-solid fa-graduation-cap"></i>
                Learning Catalog
              </span>
              <h1>Choose the right path. Join when it's open.</h1>
              <p>
                Explore structured courses and live sessions built for modern
                engineers, teams, and builders.
              </p>
            </div>
          </div>

          <form className="ac-search-panel" onSubmit={handleSubmit}>
            <label className="ac-search-field" aria-label="Search catalog">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input
                type="search"
                placeholder="Search course, session, skill..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </label>

            <select
              className="ac-filter-select"
              value={selectedType}
              onChange={(event) => selectType(event.target.value)}
              aria-label="Filter by type"
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <select
              className="ac-filter-select"
              value={selectedStatus}
              onChange={(event) => selectStatus(event.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <button className="ac-filter-button" type="submit" aria-label="Apply filters">
              <i className="fa-solid fa-filter"></i>
            </button>
          </form>
        </div>
      </section>

      <section className="ac-container ac-catalog-content">
        <div className="ac-catalog-header">
          <div>
            <h2>Available learning items</h2>
            <p>
              {isLoading
                ? "Loading learning items..."
                : `${totalResults} result${totalResults === 1 ? "" : "s"} found`}
            </p>
          </div>

          <div className="ac-filter-chips" aria-label="Current filters">
            <button
              className={!selectedType && !selectedStatus ? "is-active" : ""}
              type="button"
              onClick={() => {
                setSelectedType("");
                setSelectedStatus("");
                setPage(0);
              }}
            >
              All
            </button>
            {TYPE_OPTIONS.slice(1).map((option) => (
              <button
                key={option.value}
                className={selectedType === option.value ? "is-active" : ""}
                type="button"
                onClick={() => selectType(option.value)}
              >
                {option.label}
              </button>
            ))}
            {STATUS_OPTIONS.slice(1).map((option) => (
              <button
                key={option.value}
                className={selectedStatus === option.value ? "is-active" : ""}
                type="button"
                onClick={() => selectStatus(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {errorMessage ? (
          <div className="ac-empty-state">{errorMessage}</div>
        ) : null}

        {isLoading ? (
          <div className="ac-grid" aria-busy="true">
            {Array.from({ length: 6 }, (_, index) => (
              <article className="ac-card ac-card-skeleton" key={index}>
                <div className="ac-cover"></div>
                <div className="ac-card-body">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </article>
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="ac-grid">
            {items.map((item) => {
              const isClosed = item.status === "CLOSED";
              const coverContent = (
                <>
                  {item?.coverPicture ? (
                    <img src={item.coverPicture} alt={item.title} />
                  ) : null}
                  <span className="ac-floating-badge ac-type">
                    {getTypeLabel(item.type)}
                  </span>
                  <span
                    className={`ac-floating-badge ac-status ${
                      isClosed ? "is-closed" : "is-opened"
                    }`}
                  >
                    {getStatusLabel(item.status)}
                  </span>
                  <div className="ac-cover-title">
                    <h3>{item.title}</h3>
                  </div>
                </>
              );

              return (
              <article className="ac-card" key={`${item.type}-${item.id}`}>
                {isClosed ? (
                  <div className="ac-cover is-disabled" aria-label={`${item.title} is closed`}>
                    {coverContent}
                  </div>
                ) : (
                  <Link
                    className="ac-cover"
                    to={getDetailsPath(item)}
                    aria-label={`View ${item.title}`}
                  >
                    {coverContent}
                  </Link>
                )}

                <div className="ac-card-body">
                  <p>{getDescriptionPreview(item)}</p>

                  <div className="ac-card-meta">
                    <div className="ac-meta-pill">
                      <span>{item.type === "SESSION" ? "Start date" : "Lessons"}</span>
                      <strong>
                        {item.type === "SESSION"
                          ? getSessionStartDateLabel(item)
                          : getCourseLessonsLabel(item)}
                      </strong>
                    </div>
                    <div className="ac-meta-pill">
                      <span>{item.type === "SESSION" ? "Duration" : "Access"}</span>
                      <strong>
                        {item.type === "SESSION"
                          ? getSessionDurationLabel(item)
                          : "Live Time"}
                      </strong>
                    </div>
                  </div>

                  {isClosed ? (
                    <span className="ac-card-action is-disabled" aria-disabled="true">
                      Details unavailable <i className="fa-solid fa-lock"></i>
                    </span>
                  ) : (
                    <Link className="ac-card-action" to={getDetailsPath(item)}>
                      View Details <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  )}
                </div>
              </article>
              );
            })}
          </div>
        ) : (
          <div className="ac-empty-state">
            <h3>No courses or sessions found</h3>
            <p>Adjust your search or filters to find another learning item.</p>
          </div>
        )}

        <div className="ac-pagination-footer">
          <p>
            Showing {showingStart}-{showingEnd} of {totalResults} items
          </p>

          {totalPages > 1 ? (
            <nav className="ac-pagination" aria-label="Courses pagination">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
                aria-label="Previous page"
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>

              {pageNumbers.map((pageNumber, index) => {
                const previousPageNumber = pageNumbers[index - 1];
                const hasGap =
                  typeof previousPageNumber === "number" &&
                  pageNumber - previousPageNumber > 1;

                return (
                  <React.Fragment key={pageNumber}>
                    {hasGap ? <span className="ac-page-gap">...</span> : null}
                    <button
                      type="button"
                      className={page === pageNumber ? "is-active" : ""}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber + 1}
                    </button>
                  </React.Fragment>
                );
              })}

              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() =>
                  setPage((currentPage) => Math.min(totalPages - 1, currentPage + 1))
                }
                aria-label="Next page"
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </nav>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export default AvailablesCourse;
